import React, { useEffect, useState } from "react";
import dollarIcon from '../../../assets/icons/assets/dollar-symbol.png'
import ltcIcon from '../../../assets/icons/assets/litecoin-ltc-logo.png'
import axios from "axios";
import "./MyCheck.css";
import { useTranslation } from "react-i18next";

const MyCheck = ({ data, cursCb }) => {
    const { t } = useTranslation();
    const [prices, setPrices] = useState({});
    const [loadingStatus, setLoadingStatus] = useState({});
    const [btcPrice, setBtcPrice] = useState(null);

    const fetchPrice = async (currency) => {
        const ticker = currency.toUpperCase();
        if (ticker === 'USD' || ticker === 'USDT') return 1;
        if (ticker === 'RUB') return cursCb || 90;

        try {
            const response = await axios.get(`https://api-ru.fynex.cc/rates/rate`, {
                params: { ticker: ticker, coin: 'USDT' }
            });
            const val = parseFloat(response.data);
            return isNaN(val) ? 0 : val;
        } catch (error) {
            console.error(error);
            return 0; 
        }
    };

    useEffect(() => {
        if (!data) return;

        const loadPrices = async () => {
            const currenciesToLoad = new Set();

            data.forEach(group => {
                if (group.data) {
                    group.data.forEach(coin => {
                        const ticker = coin[0];
                        if (loadingStatus[ticker] !== 'success' && loadingStatus[ticker] !== 'loading') {
                            currenciesToLoad.add(ticker);
                        }
                    });
                }
            });

            if (loadingStatus['btc'] !== 'success' && loadingStatus['btc'] !== 'loading') {
                currenciesToLoad.add('btc');
            }

            if (currenciesToLoad.size === 0) return;

            setLoadingStatus(prev => {
                const newStatus = { ...prev };
                currenciesToLoad.forEach(c => newStatus[c] = 'loading');
                return newStatus;
            });

            const promises = Array.from(currenciesToLoad).map(async (currency) => {
                const price = await fetchPrice(currency);
                return { currency, price };
            });

            const results = await Promise.all(promises);

            setPrices(prev => {
                const newPrices = { ...prev };
                let newBtc = btcPrice;
                results.forEach(({ currency, price }) => {
                    newPrices[currency] = price;
                    if (currency.toLowerCase() === 'btc') newBtc = price;
                });
                if (newBtc !== btcPrice) setBtcPrice(newBtc);
                return newPrices;
            });

            setLoadingStatus(prev => {
                const newStatus = { ...prev };
                results.forEach(({ currency }) => {
                    newStatus[currency] = 'success';
                });
                return newStatus;
            });
        };

        loadPrices();
    }, [data, cursCb]);

    const calculateTotalAmount = (itemsData) => {
        let totalUsd = 0;
        let totalBtc = 0;
        if (!itemsData) return { totalUsd, totalBtc };

        itemsData.forEach(([currency, amount]) => {
            const price = prices[currency] || 0;
            const usdValue = currency === 'rub' ? amount / price : amount * price;
            totalUsd += usdValue;
            if (btcPrice) {
                totalBtc += usdValue / btcPrice;
            }
        });
        return { totalUsd, totalBtc };
    };

    const formatCrypto = (val) => {
        if (!val) return "0.00";
        return val < 1 ? val.toFixed(7).replace(/\.?0+$/, '') : val.toFixed(3).replace(/\.?0+$/, '');
    }

    const getIcon = (currency) => {
        if (currency === 'usd') return <img src={dollarIcon} alt="usd" />;
        if (currency === 'ltc') return <img src={ltcIcon} alt="ltc" />;
        return <img src={`https://api-ru.fynex.cc/api/static/icons/${currency}.png`} alt={currency} onError={(e) => e.target.style.opacity = '0 '} />;
    };

    return (
        <div className="MyCheck">
            <h2 className="mycheck-headline">{t("myCheck.headline")}</h2>
            
            {data.map((items, idx) => {
                const { totalUsd, totalBtc } = calculateTotalAmount(items.data);
                
                const isParentLoading = items.isLoading; 
                
                const arePricesReady = items.data 
                    ? items.data.every(([ticker]) => loadingStatus[ticker] === 'success') 
                    : true;

                const isBtcReady = loadingStatus['btc'] === 'success';
                const showSkeleton = isParentLoading || (!arePricesReady && items.data?.length > 0) || (!isBtcReady && items.data?.length > 0);

                return (
                    <div key={idx} className="checkItem">
                        <div
                            className="checkItemWrapper"
                            onClick={(e) => {
                                const parent = e.target.closest(".checkItem");
                                if (parent) parent.classList.toggle("checkItemWrapperActive");
                            }}
                        >
                            <div className="nameChek">
                                <h2>{items.name}</h2>
                            </div>
                            <div className="dataChek">
                                {showSkeleton ? (
                                    <div style={{display:'flex', flexDirection:'column', alignItems:'flex-end', gap:'6px'}}>
                                        <div className="skeleton skeleton-title" style={{width: '120px', height: '20px'}}></div>
                                        <div className="skeleton skeleton-text" style={{width: '80px', height: '14px'}}></div>
                                    </div>
                                ) : (
                                    <>
                                        <h2>{totalBtc.toFixed(6)} BTC</h2>
                                        <p>{totalUsd.toFixed(2)} USD</p>
                                    </>
                                )}
                            </div>
                        </div>
                        
                        <div className="checkItemContent">
                            <ul>
                                {items.data && items.data.map((courseData, i) => {
                                    const currencyName = courseData[0];
                                    const count = courseData[1];
                                    const currentPrice = prices[currencyName] || 0;
                                    const coinReady = loadingStatus[currencyName] === 'success';

                                    let valueInUsd = 0;
                                    if (coinReady) {
                                        valueInUsd = currencyName === 'rub' ? count / currentPrice : count * currentPrice;
                                    }

                                    return (
                                        <li key={i}>
                                            <div className="course">
                                                {getIcon(currencyName)}
                                                <div className="courseName">
                                                    <h2>{currencyName.toUpperCase()}</h2>
                                                    <p>{currencyName}</p>
                                                </div>
                                            </div>
                                            <div className="value">
                                                {!coinReady ? (
                                                     <div style={{width: '80px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px'}}>
                                                         <div className="skeleton skeleton-text" style={{width: '70px', height: '14px'}}></div>
                                                         <div className="skeleton skeleton-text" style={{width: '50px', height: '12px'}}></div>
                                                     </div>
                                                ) : (
                                                    <>
                                                        <h2>{`${valueInUsd.toFixed(2)} USD`}</h2>
                                                        <p>{formatCrypto(count)}</p>
                                                    </>
                                                )}
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default MyCheck;



















// import React, { useEffect, useState } from "react";
// import dollarIcon from '../../../assets/icons/assets/dollar-symbol.png'
// import ltcIcon from '../../../assets/icons/assets/litecoin-ltc-logo.png'
// import axios from "axios";
// import "./MyCheck.css";
// import { useTranslation } from "react-i18next";

// const MyCheck = ({ data, cursCb }) => {
//     const {t} = useTranslation()
//     const [prices, setPrices] = useState({}); // Храним цены валют
//     const [btcPrice, setBtcPrice] = useState(null); // Храним цену BTC
//     const [generalSum, setGeneralSum] = useState(0)

//     // Функция для загрузки курса
//     const fetchPrice = async (currency) => {
//         try {
//             const response = await axios.get(`https://api.binance.com/api/v3/ticker/price?symbol=${currency}USDT`);
//             return parseFloat(response.data.price);
//         } catch (error) {
//             console.error(`Ошибка при загрузке курса для ${currency}:`, error);
//             return null;
//         }
//     };

//     const returnCourseBigName = (courseName, count) => {
//         const price = prices[courseName];
//         if(courseName == "rub") return price ? `${(count / price).toFixed(2)} USD` : "Loading...";
//         return price ? `${(price * count).toFixed(2)} USD` : "Loading...";
//     };

//     // Функция для расчета общей суммы
//     const calculateTotalAmount = (data, prices) => {
//         let totalUsd = 0;
//         let totalBtc = 0;
//         data?.forEach(([currency, amount]) => {
//             const price = prices[currency];
//             if (price) {
//                 totalUsd += price * amount;
//                 if (currency === "btc") {
//                     totalBtc += amount;
//                 } else if (btcPrice) {
//                     totalBtc += (price * amount) / btcPrice;
//                 }
//             }
//         });
//         return { totalUsd, totalBtc };
//     };

//     useEffect(() => {
//         const loadPrices = async () => {
//             const uniqueCurrencies = [
//                 ...new Set(
//                     data
//                         .filter(item => item.data)
//                         .flatMap(item => item.data.map(course => course[0]))
//                 ),
//             ];

//             const fetchedPrices = {};
//             for (const currency of uniqueCurrencies) {
//                 let price;
//                 if (currency !== "usd" && currency !== "usdt" && currency !== "rub") {
//                     price = await fetchPrice(currency.toUpperCase());
//                 } else if (currency === "usd" || currency === "usdt") {
//                     price = 1;
//                 } else if (currency === 'rub') {
//                     price = cursCb;
//                 }
//                 if (price !== null) {
//                     fetchedPrices[currency] = price;
//                 }
//             }

//             setPrices(fetchedPrices);
//             const btcPrice = await fetchPrice("BTC");
//             setBtcPrice(btcPrice);
//         };

//         loadPrices();
//     }, [data, cursCb]);

//     return (
//         <div className="MyCheck">
//             <h2>{t("myCheck.headline")}</h2>
//             {data.map((items, idx) => {
//                 const { totalUsd, totalBtc } = calculateTotalAmount(items.data, prices);
//                 return (
//                     <div key={idx} className="checkItem">
//                         <div
//                             className="checkItemWrapper"
//                             onClick={(e) => {
//                                 const parent = e.target.closest(".checkItem");
//                                 if (parent) {
//                                     parent.classList.toggle("checkItemWrapperActive");
//                                 }
//                             }}
//                         >
//                             <div className="nameChek">
//                                 <h2>{items.name}</h2>
//                             </div>
//                             <div className="dataChek">
//                                 <h2>{totalBtc.toFixed(6)} BTC</h2>
//                                 <p>{totalUsd.toFixed(2)} USD</p>
//                             </div>
//                         </div>
//                         <ul>
//                             {items.data &&
//                                 items?.data.map((courseData, idx) => (
//                                     <li key={idx}>
//                                         <div className="course">
//                                             {/* {returnIcon(courseData[0])} */}
//                                             {
//                                                 courseData[0] == 'usd' || courseData[0] == 'ltc' ?
//                                                 courseData[0] == 'usd' ? <img src={dollarIcon} alt="" /> : <img src={ltcIcon} alt="" />
//                                                 :
//                                                 <img src={`https://api-ru.fynex.cc/api/static/icons/${courseData[0]}.png`} alt="" />

//                                             }
//                                             <div className="courseName">
//                                                 <h2>{courseData[0].toUpperCase()}</h2>
//                                                 <p>{courseData[0]}</p>
//                                             </div>
//                                         </div>
//                                         <div className="value">
//                                             <h2>{returnCourseBigName(courseData[0], courseData[1])}</h2>
//                                             {/* <p>{courseData[1]}</p> */}
//                                             <p>
//                                                 {
//                                                 courseData[1] ? 
//                                                 courseData[1] < 1 ? courseData[1].toFixed(7).replace(/\.?0+$/, '') : courseData[1].toFixed(3).replace(/\.?0+$/, '')
//                                                 : ""
//                                                 } 
//                                             </p>
//                                         </div>
//                                     </li>
//                                 ))}
//                         </ul>
//                     </div>
//                 );
//             })}
//         </div>
//     );
// };

// export default MyCheck;